
import React, { useCallback, lazy, Suspense } from 'react';
import PublicHeader from '@/components/PublicHeader';
import SharedHeader from '@/components/SharedHeader';
import SearchAutocomplete from '@/components/search/SearchAutocomplete';
import { useState } from 'react';

// Lazy load SearchResults to defer heavy dependencies until search is performed
const SearchResults = lazy(() => import('@/components/search/SearchResults'));
import { type GeneratedAddress } from '@/types/database';
import { type LocationSearchResult } from '@/types/locationSearch';
import { useBranding } from '@/hooks/useBranding';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/hooks/useAuth';
import { useActiveAnnouncement } from '@/hooks/useActiveAnnouncement';
import { Card, CardContent } from '@/components/ui/card';
import { X } from 'lucide-react';
import { parseLinksInText } from '@/utils/linkParser';
import GetTheAppPrompt from '@/components/GetTheAppPrompt';

const PublicHome = React.memo(() => {
  const [searchResult, setSearchResult] = useState<GeneratedAddress | null>(null);
  const [locationResult, setLocationResult] = useState<LocationSearchResult | null>(null);
  const [alternativeLocations, setAlternativeLocations] = useState<LocationSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  
  const { copyrightText } = useBranding();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: announcement, isLoading: announcementLoading } = useActiveAnnouncement();

  const handleSelectResult = useCallback((result: GeneratedAddress) => {
    setSearchResult(result);
    setLocationResult(null);
    setIsSearching(false);
  }, []);

  const handleLocationResult = useCallback((result: LocationSearchResult, alternatives?: LocationSearchResult[]) => {
    setLocationResult(result);
    setAlternativeLocations(alternatives || []);
    setSearchResult(null);
    setIsSearching(false);
  }, []);

  const handleSearching = useCallback((searching: boolean) => {
    setIsSearching(searching);
    if (searching) {
      setSearchResult(null);
      setLocationResult(null);
    }
  }, []);

  const handleAddressUpdated = useCallback((updatedAddress: GeneratedAddress) => {
    setSearchResult(updatedAddress);
  }, []);

  const hasSearched = searchResult || locationResult || isSearching;

  // Reserve fixed height for announcement area to prevent CLS.
  // This space is always present whether the announcement is loading, shown, or dismissed.
  const showAnnouncementArea = !hasSearched;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      {user ? <SharedHeader /> : <PublicHeader />}
      
      <div className="flex flex-col items-center justify-center flex-1 px-4 py-8 min-h-[calc(100vh-80px)] sm:min-h-[calc(100vh-140px)]">
        
        {/* Announcement area — fixed min-height to prevent layout shift */}
        {showAnnouncementArea && (
          <div className="w-full max-w-2xl mb-6 min-h-[76px]">
            {!announcementLoading && announcement && showAnnouncement && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-3 sm:p-4 overflow-hidden">
                  <div className="flex items-start justify-between gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-blue-900 mb-2 break-words">{announcement.subject}</h3>
                      <p className="text-blue-800 text-sm leading-relaxed break-words whitespace-pre-wrap">
                        {parseLinksInText(announcement.message)}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAnnouncement(false)}
                      className="text-blue-600 hover:text-blue-800 p-1 flex-shrink-0"
                      aria-label="Dismiss announcement"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        
        <div className="w-full max-w-2xl">
          <SearchAutocomplete
            onSelectResult={handleSelectResult}
            onLocationResult={handleLocationResult}
            onSearching={handleSearching}
            onSearchModeChange={(mode) => {
              if (mode !== 'directions') return;
              setSearchResult(null);
              setLocationResult(null);
              setAlternativeLocations([]);
              setIsSearching(false);
            }}
            placeholder="Search by address or property name..."
          />
        </div>

        {/* App Store and Play download links stay on home, including after a search. */}
        <GetTheAppPrompt />
        
        {/* Results Container */}
        {hasSearched && (
          <div className="w-full max-w-4xl mt-8">
            <div className="mb-4">
              <button
                onClick={() => {
                  setSearchResult(null);
                  setLocationResult(null);
                  setAlternativeLocations([]);
                  setIsSearching(false);
                }}
                className="text-emerald-600 hover:text-emerald-700 font-medium text-sm flex items-center gap-1 transition-colors"
              >
                ← Back to Search
              </button>
            </div>
            <Suspense fallback={
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
              </div>
            }>
              <SearchResults 
                isSearching={isSearching}
                searchResult={searchResult}
                locationResult={locationResult}
                alternativeLocations={alternativeLocations}
                onSwapLocation={(newMain) => {
                  if (!locationResult) return;
                  const updatedAlts = [locationResult, ...alternativeLocations.filter(a => a.placeId !== newMain.placeId)];
                  setLocationResult(newMain);
                  setAlternativeLocations(updatedAlts.slice(0, 3));
                }}
                notFound={false}
                onAddressUpdated={handleAddressUpdated}
              />
            </Suspense>
          </div>
        )}
      </div>

      <footer className="w-full py-3 sm:py-4 text-center bg-gray-50 border-t border-gray-200 mt-auto">
        <p className="text-gray-400 text-xs">
          © 2025 {copyrightText}
        </p>
      </footer>
    </div>
  );
});

PublicHome.displayName = 'PublicHome';

export default PublicHome;
